'use client';

/**
 * Partner-scoped merchant CRM keys page
 * (mounted at `/partner-admin/:partner/crm-keys`).
 *
 * Lets a CRM reseller (e.g. BNP) provision an `org:admin` CRM key
 * for each of its merchant `accounts`. The key (`sk_crm_…`) is the
 * Bearer token the merchant then uses against `/api/public/v1/*`.
 *
 * Structure mirrors the IAM Merchants tab:
 *   - merchant list comes from `iamOrgs.list` (scope-clamped
 *     server-side — a partneradmin only ever sees their partner's
 *     merchants);
 *   - clicking a merchant opens a side sheet listing its
 *     partner-provisioned keys, with create (reveal-once) + revoke.
 *
 * Access control mirrors the Team page (`usePartnerAdmin`):
 *   - superadmin: any partner via the URL `:partner` param.
 *   - partneradmin: only their own `whitelabelOwnership`.
 *   - everyone else: denied.
 *
 * Security: the UI only gates what it renders. The IAM use-cases
 * behind both tRPC procedures re-enforce the scope clamp
 * (`canManageOrgMembership`) and per-account ownership, so a
 * hand-crafted request can't reach another partner's merchant.
 */
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Building2,
    Check,
    ChevronLeft,
    ChevronRight,
    Copy,
    KeySquare,
    Loader2,
    Plus,
    Search,
    ShieldAlert,
    Trash2,
    X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { trpc } from '@/lib/api/trpc';

const PAGE_SIZE = 50;

type OrgRow = {
    accountId: string;
    orgClerkId: string;
    orgClerkName: string | null;
    orgClerkSlug: string | null;
    kind: 'self_serve' | 'partner_managed';
    createdAt: Date | string | null;
    partnerId: string | null;
    partnerSlug: string | null;
    partnerName: string | null;
};

type CrmKey = {
    object: 'crm_key';
    id: string;
    accountId: string;
    name: string;
    prefix: string;
    orgRole: string;
    revoked: boolean;
    createdAt: string;
    expiresAt: string | null;
};

export default function PartnerAdminCrmKeysPage() {
    const { t } = useTranslation('partnerApiKeys');
    const { partner: partnerParam } = useParams<{ partner: string }>();
    const { canView, partner, isLoading: loadingRole } =
        usePartnerAdmin(partnerParam);

    if (loadingRole) {
        return (
            <div className="p-6 text-sm text-muted-foreground">{t('loading')}</div>
        );
    }

    if (!canView || !partner) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h2 className="mt-4 text-xl font-semibold">{t('accessDenied')}</h2>
                    <p className="mt-2 max-w-md text-sm text-muted-foreground">
                        {t('accessDeniedBody', { partner: partnerParam ?? '(unset)' })}
                    </p>
                </div>
            </div>
        );
    }

    return <MerchantCrmKeysView partner={partner} />;
}

function MerchantCrmKeysView({ partner }: { partner: string }) {
    const { t } = useTranslation('partnerApiKeys');
    const [q, setQ] = React.useState('');
    const [qDebounced, setQDebounced] = React.useState('');
    const [page, setPage] = React.useState(0);
    const [selected, setSelected] = React.useState<OrgRow | null>(null);

    React.useEffect(() => {
        const id = setTimeout(() => setQDebounced(q), 250);
        return () => clearTimeout(id);
    }, [q]);
    React.useEffect(() => {
        setPage(0);
    }, [qDebounced]);

    const orgsQuery = trpc.iamOrgs.list.useQuery(
        {
            q: qDebounced || undefined,
            partnerSlug: partner,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        },
        { staleTime: 15_000 },
    );

    const total = orgsQuery.data?.total ?? 0;
    const rows = (orgsQuery.data?.rows ?? []) as OrgRow[];
    const hasFilter = !!qDebounced;

    return (
        <div className="space-y-6">
            <header className="space-y-1.5 border-b pb-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold tracking-tight">
                        {t('title')}
                    </h2>
                    <Badge variant="secondary" className="text-xs uppercase">
                        {partner}
                    </Badge>
                </div>
                <p className="max-w-3xl text-sm text-muted-foreground">
                    {t('description')}
                </p>
            </header>

            <section
                aria-label={t('filters')}
                className="flex flex-wrap items-center gap-2"
            >
                <div className="relative min-w-0 flex-1 sm:max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder={t('searchPlaceholder')}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="h-9 pl-9 pr-8"
                    />
                    {q && (
                        <button
                            type="button"
                            onClick={() => setQ('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={t('clearSearch')}
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </section>

            <section aria-label={t('merchants')} className="space-y-3">
                {orgsQuery.isLoading ? (
                    <div className="overflow-hidden rounded-lg border bg-card">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-4 border-b px-4 py-3 last:border-0"
                            >
                                <div className="flex-1 space-y-1.5">
                                    <Skeleton className="h-3.5 w-48" />
                                    <Skeleton className="h-3 w-32" />
                                </div>
                                <Skeleton className="h-7 w-24" />
                            </div>
                        ))}
                    </div>
                ) : orgsQuery.error ? (
                    <Alert variant="destructive">
                        <AlertTitle>{t('merchantsLoadError')}</AlertTitle>
                        <AlertDescription>{orgsQuery.error.message}</AlertDescription>
                    </Alert>
                ) : rows.length === 0 ? (
                    <div className="rounded-lg border border-dashed bg-card/40 px-6 py-14 text-center">
                        <Building2 className="mx-auto h-9 w-9 text-muted-foreground/60" />
                        <h3 className="mt-3 text-sm font-medium">
                            {t('noMerchantsTitle')}
                        </h3>
                        <p className="mt-1.5 text-sm text-muted-foreground">
                            {hasFilter ? t('noMerchantsFiltered') : t('noMerchantsBody')}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-hidden rounded-lg border bg-card">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                        <th className="px-4 py-2.5 text-left">
                                            {t('colMerchant')}
                                        </th>
                                        <th className="hidden px-4 py-2.5 text-left md:table-cell">
                                            {t('colCreated')}
                                        </th>
                                        <th className="w-px px-4 py-2.5" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {rows.map((o) => (
                                        <tr
                                            key={o.accountId}
                                            onClick={() => setSelected(o)}
                                            className={
                                                'cursor-pointer transition-colors hover:bg-muted/30 ' +
                                                (selected?.accountId === o.accountId
                                                    ? 'bg-muted/50'
                                                    : '')
                                            }
                                        >
                                            <td className="max-w-0 px-4 py-3">
                                                <div className="truncate font-medium text-foreground">
                                                    {o.orgClerkName || (
                                                        <span className="text-muted-foreground">—</span>
                                                    )}
                                                </div>
                                                <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                                                    {o.orgClerkSlug ?? o.accountId}
                                                </div>
                                            </td>
                                            <td className="hidden whitespace-nowrap px-4 py-3 text-xs text-muted-foreground md:table-cell">
                                                {o.createdAt
                                                    ? new Date(o.createdAt).toLocaleDateString()
                                                    : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 px-2 text-xs"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelected(o);
                                                    }}
                                                >
                                                    <KeySquare className="mr-1.5 h-3.5 w-3.5" />
                                                    {t('manageKeys')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <div className="tabular-nums">
                                {t('paginationRange', {
                                    from: page * PAGE_SIZE + 1,
                                    to: Math.min(total, (page + 1) * PAGE_SIZE),
                                    total: total.toLocaleString(),
                                })}
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={page === 0 || orgsQuery.isFetching}
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    aria-label={t('prevPage')}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={
                                        (page + 1) * PAGE_SIZE >= total || orgsQuery.isFetching
                                    }
                                    onClick={() => setPage((p) => p + 1)}
                                    aria-label={t('nextPage')}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </section>

            <MerchantKeysSheet
                partner={partner}
                org={selected}
                open={selected !== null}
                onOpenChange={(o) => !o && setSelected(null)}
            />
        </div>
    );
}

/**
 * Side sheet: the CRM keys of a single merchant account. Lists the
 * partner-provisioned keys and exposes create (reveal-once) + revoke.
 */
function MerchantKeysSheet({
    partner,
    org,
    open,
    onOpenChange,
}: {
    partner: string;
    org: OrgRow | null;
    open: boolean;
    onOpenChange: (o: boolean) => void;
}) {
    const { t } = useTranslation('partnerApiKeys');
    const utils = trpc.useUtils();
    const [revealToken, setRevealToken] = React.useState<{
        name: string;
        token: string;
    } | null>(null);

    const keysQuery = trpc.partners.listMerchantCrmKeys.useQuery(
        org ? { partner, accountId: org.accountId } : { partner, accountId: 'acc_x' },
        { enabled: open && !!org, staleTime: 5_000 },
    );

    const createMutation = trpc.partners.createMerchantCrmKey.useMutation({
        onSuccess: (created) => {
            setRevealToken({ name: created.name, token: created.token });
            if (org)
                utils.partners.listMerchantCrmKeys.invalidate({
                    partner,
                    accountId: org.accountId,
                });
        },
        onError: (e) => toast.error(e.message),
    });

    const revokeMutation = trpc.partners.revokeMerchantCrmKey.useMutation({
        onSuccess: () => {
            toast.success(t('revokeSuccess'));
            if (org)
                utils.partners.listMerchantCrmKeys.invalidate({
                    partner,
                    accountId: org.accountId,
                });
        },
        onError: (e) => toast.error(e.message),
    });

    if (!org) return null;

    const keys = (keysQuery.data ?? []) as CrmKey[];
    const activeKeys = keys.filter((k) => !k.revoked);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
                <SheetHeader className="border-b px-6 pb-5 pr-12 pt-6">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted/40">
                            <KeySquare className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1 space-y-1">
                            <SheetTitle className="truncate text-base leading-tight">
                                {org.orgClerkName ?? org.orgClerkSlug ?? org.accountId}
                            </SheetTitle>
                            <SheetDescription className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                <span className="font-mono text-[10px] text-muted-foreground/80">
                                    {org.accountId}
                                </span>
                            </SheetDescription>
                        </div>
                    </div>
                </SheetHeader>

                <div className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
                    <Alert>
                        <KeySquare className="h-4 w-4" />
                        <AlertTitle>{t('keysInfoTitle')}</AlertTitle>
                        <AlertDescription className="text-xs">
                            {t('keysInfoBody')}
                        </AlertDescription>
                    </Alert>

                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('keysHeading')}
                            </h3>
                            <span className="text-xs tabular-nums text-muted-foreground/70">
                                {activeKeys.length}
                            </span>
                        </div>
                        <Button
                            size="sm"
                            className="h-8"
                            disabled={createMutation.isPending}
                            onClick={() =>
                                createMutation.mutate({ partner, accountId: org.accountId })
                            }
                        >
                            {createMutation.isPending ? (
                                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Plus className="mr-1.5 h-3.5 w-3.5" />
                            )}
                            {t('createKey')}
                        </Button>
                    </div>

                    {keysQuery.isLoading ? (
                        <div className="divide-y rounded-md border bg-card">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                                    <div className="flex-1 space-y-1.5">
                                        <Skeleton className="h-3.5 w-40" />
                                        <Skeleton className="h-3 w-56" />
                                    </div>
                                    <Skeleton className="h-8 w-8" />
                                </div>
                            ))}
                        </div>
                    ) : keysQuery.error ? (
                        <Alert variant="destructive">
                            <AlertTitle>{t('keysLoadError')}</AlertTitle>
                            <AlertDescription>{keysQuery.error.message}</AlertDescription>
                        </Alert>
                    ) : keys.length === 0 ? (
                        <div className="rounded-md border border-dashed bg-card/40 px-6 py-12 text-center">
                            <KeySquare className="mx-auto h-8 w-8 text-muted-foreground/60" />
                            <p className="mt-3 text-sm font-medium">{t('noKeysTitle')}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {t('noKeysBody')}
                            </p>
                        </div>
                    ) : (
                        <ul className="divide-y rounded-md border bg-card">
                            {keys.map((k) => {
                                const revoking =
                                    revokeMutation.isPending &&
                                    revokeMutation.variables?.keyId === k.id;
                                return (
                                    <li
                                        key={k.id}
                                        className="flex items-center gap-3 px-4 py-3"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="truncate text-sm font-medium leading-tight">
                                                    {k.name}
                                                </span>
                                                <Badge
                                                    variant="outline"
                                                    className="font-mono text-[10px]"
                                                >
                                                    {k.orgRole}
                                                </Badge>
                                                {k.revoked && (
                                                    <Badge
                                                        variant="secondary"
                                                        className="text-[10px]"
                                                    >
                                                        {t('revoked')}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                                {t('createdAt', {
                                                    date: new Date(k.createdAt).toLocaleString(),
                                                })}
                                            </div>
                                        </div>
                                        {!k.revoked && (
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                                onClick={() => {
                                                    if (!confirm(t('revokeConfirm', { name: k.name })))
                                                        return;
                                                    revokeMutation.mutate({ partner, keyId: k.id });
                                                }}
                                                disabled={revoking}
                                                aria-label={t('revoke')}
                                            >
                                                {revoking ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                )}
                                            </Button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <SheetFooter className="border-t bg-muted/30 px-6 py-3 sm:flex-row sm:justify-end">
                    <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                        {t('close')}
                    </Button>
                </SheetFooter>

                <RevealTokenDialog
                    reveal={revealToken}
                    onClose={() => setRevealToken(null)}
                />
            </SheetContent>
        </Sheet>
    );
}

/**
 * Reveal-once dialog. The secret token is shown a single time right
 * after creation — there is no way to retrieve it again, mirroring
 * the rest of the platform.
 */
function RevealTokenDialog({
    reveal,
    onClose,
}: {
    reveal: { name: string; token: string } | null;
    onClose: () => void;
}) {
    const { t } = useTranslation('partnerApiKeys');
    const [copied, setCopied] = React.useState(false);

    React.useEffect(() => {
        if (reveal) setCopied(false);
    }, [reveal]);

    const copy = async () => {
        if (!reveal) return;
        try {
            await navigator.clipboard.writeText(reveal.token);
            setCopied(true);
            toast.success(t('copied'));
        } catch {
            toast.error(t('copyFailed'));
        }
    };

    return (
        <Dialog open={reveal !== null} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('revealTitle')}</DialogTitle>
                    <DialogDescription>{t('revealDescription')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="text-xs font-medium text-muted-foreground">
                        {reveal?.name}
                    </div>
                    <div className="flex items-center gap-2">
                        <code className="block flex-1 overflow-x-auto rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs">
                            {reveal?.token}
                        </code>
                        <Button
                            size="icon"
                            variant="outline"
                            className="h-9 w-9 shrink-0"
                            onClick={copy}
                            aria-label={t('copyToken')}
                        >
                            {copied ? (
                                <Check className="h-4 w-4 text-green-600" />
                            ) : (
                                <Copy className="h-4 w-4" />
                            )}
                        </Button>
                    </div>
                    <Alert>
                        <ShieldAlert className="h-4 w-4" />
                        <AlertDescription className="text-xs">
                            {t('revealWarning')}
                        </AlertDescription>
                    </Alert>
                </div>
                <DialogFooter>
                    <Button onClick={onClose}>{t('revealDone')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
